/**
 * LocalStorage-backed shopping cart hook.
 *
 * Cart item shape:
 * {
 *   uid,          // unique id for this cart line (crypto.randomUUID())
 *   product_id,
 *   product_name,
 *   category,
 *   option_ids,   // [4, 12, 30, ...]
 *   selection,    // {size: "3.5 x 2", qty: "500", ...} (readable names)
 *   unit_price,   // per configuration, from live pricing API
 *   quantity,     // number of *configurations* to order (usually 1)
 *   currency,     // "CAD"
 *   package_info, // optional shipping metadata
 * }
 */
import { useCallback, useEffect, useState } from "react";

const KEY = "tcsw_cart_v1";

function readCart() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeCart(items) {
  try {
    localStorage.setItem(KEY, JSON.stringify(items));
    window.dispatchEvent(new CustomEvent("tcsw:cart-updated"));
  } catch {}
}

export function useCart() {
  const [items, setItems] = useState(readCart);

  useEffect(() => {
    const sync = () => setItems(readCart());
    window.addEventListener("storage", sync);
    window.addEventListener("tcsw:cart-updated", sync);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener("tcsw:cart-updated", sync);
    };
  }, []);

  const addItem = useCallback((item) => {
    const withUid = { ...item, uid: (crypto.randomUUID && crypto.randomUUID()) || String(Date.now() + Math.random()) };
    const next = [...readCart(), withUid];
    writeCart(next);
    setItems(next);
    return withUid;
  }, []);

  const removeItem = useCallback((uid) => {
    const next = readCart().filter((it) => it.uid !== uid);
    writeCart(next);
    setItems(next);
  }, []);

  const updateQuantity = useCallback((uid, quantity) => {
    const q = Math.max(1, parseInt(quantity, 10) || 1);
    const next = readCart().map((it) => (it.uid === uid ? { ...it, quantity: q } : it));
    writeCart(next);
    setItems(next);
  }, []);

  const clear = useCallback(() => {
    writeCart([]);
    setItems([]);
  }, []);

  const subtotal = items.reduce((sum, it) => sum + (Number(it.unit_price) || 0) * (Number(it.quantity) || 1), 0);
  const itemCount = items.reduce((sum, it) => sum + (Number(it.quantity) || 1), 0);

  return { items, addItem, removeItem, updateQuantity, clear, subtotal, itemCount };
}
